import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  MultiSelectOption,
  PaginationV2,
  TableFilter,
  TableFiltersSection,
  TableSortMode,
  TableSortModeValue,
  TableV2,
  useDimensions,
} from '@equip.health/ui';

import { loadingRows } from './InquiryTableRow';
import InquiryFilter from '../InquiryFilter';
import { useProvidersFilterData } from './useProvidersFilterData';
import { InquiryEvents } from '~/analytics';
import { DEFAULT_PAGINATION_CONFIG, PAGE_LAYOUT } from '~/lib/constants';
import {
  channelOptions,
  channelTypeMenuOptions,
} from '~/lib/constants/inquiry.constants';
import { CareTeamProviderType } from '~/lib/constants/patients.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import {
  useInquiryListDispatch,
  useInquiryListPaginationSelector,
  useIsConflictErrorModalOpenSelector,
} from '~/store/reducers/inquiryList';
import { useGetInformationGatheredSourcesQuery } from '~/store/services/inquiry';
import { QCAppointmentOptions } from './utils';
import InquiryListConflictErrorModal from '~/components/maud/inquiries/InquiryViewsTable/InquiryListConflictErrorModal';
import { useInquiryTableData } from './useInquiryTableData';

const tableFiltersForMixpanel = (tableFilters: TableFilter[]) => {
  return tableFilters.map((f) => {
    return {
      Filter: f.title,
      Values: f.filters.map((v) => v.value),
    };
  });
};

const InquiryViewsTable = ({ viewCode }) => {
  const { trackEvent } = useAnalytics();
  const { pageNumber, pageSize } = useInquiryListPaginationSelector();
  const {
    setPageNumber,
    setPageSize,
    resetPagination,
    reloadInquiryList,
    setIsConflictErrorModalOpen,
  } = useInquiryListDispatch();

  const [sortKey, setSortKey] = useState<string>();
  const [sortMode, setSortMode] = useState<TableSortModeValue>();
  const [timeStamp, setTimeStamp] = useState<number>(0);
  const [isOpenFilter, setIsOpenFilter] = useState<boolean>(false);

  const {
    columns,
    coverageStatusOptions,
    inquiryTableFilters,
    isTableLoading,
    medicalStatusOptions,
    payors,
    rows,
    selectedInquiryId,
    setInquiryTableFilters,
    timezones,
    totalCount,
    treatmentProgramOptions,
  } = useInquiryTableData(viewCode, sortKey, sortMode, pageNumber, pageSize);

  const { data: informationGatheredSources } =
    useGetInformationGatheredSourcesQuery();

  const isConflictErrorModalOpen = useIsConflictErrorModalOpenSelector();

  const psrProviders = useProvidersFilterData({
    providerType: CareTeamProviderType.PATIENT_SERVICE_REPRESENTATIVE,
  });

  const rcProviders = useProvidersFilterData({
    providerType: CareTeamProviderType.PROVIDER_PARTNERSHIPS,
  });

  const asProviders = useProvidersFilterData({
    providerType: CareTeamProviderType.ADMISSIONS_SPECIALIST,
  });

  const aoProviders = useProvidersFilterData({
    providerType: CareTeamProviderType.ADMISSIONS_OPERATIONS_COORDINATOR,
  });

  const supervisorProviders = useProvidersFilterData({
    providerType: CareTeamProviderType.ADMISSIONS_SUPERVISOR,
  });

  const handleColumnSort = (
    columnKey: string,
    sortMode: TableSortModeValue,
  ) => {
    setPageNumber(1);
    if (sortMode === TableSortMode.none) {
      setSortKey(undefined);
      setSortMode(undefined);
    } else {
      setSortKey(columnKey);
      setSortMode(sortMode);
    }
    trackEvent(InquiryEvents.tableSorted, { 'Sort Mode': sortMode });
  };

  const handleFiltersChange = useCallback(
    (filters: TableFilter[]) => {
      resetPagination();
      setInquiryTableFilters(filters);
      setTimeStamp(timeStamp + 1);
      setIsOpenFilter(false);
      trackEvent(InquiryEvents.tableFiltered, {
        Filters: tableFiltersForMixpanel(filters),
      });
    },
    [resetPagination, setInquiryTableFilters, timeStamp, trackEvent],
  );

  useEffect(() => {
    handleFiltersChange(inquiryTableFilters);
  }, [viewCode]);

  const handlePageChange = (pageNumber: number, source: string) => {
    setPageNumber(pageNumber);
    trackEvent(InquiryEvents.tablePageChanged, {
      Page: pageNumber,
      Source: source,
    });
  };

  const handleItemPerPageChange = (pageSize: number) => {
    setPageSize(pageSize);
    trackEvent(InquiryEvents.tablePageSizeChanged, { 'Page Size': pageSize });
  };

  const tableRef = useRef();
  const tableYPosition = useDimensions(tableRef, true)?.borderBox.top;
  const windowHeight = window?.innerHeight;
  const tableHeight =
    windowHeight - tableYPosition - DEFAULT_PAGINATION_CONFIG.paginationHeight;

  const informationGatheredSourcesOptions = useMemo<MultiSelectOption[]>(() => {
    return informationGatheredSources?.map((source) => ({
      id: source.id,
      label: source.name,
      value: source.name,
    }));
  }, [informationGatheredSources]);

  return (
    <ChakraFlex data-testid="inquiry-views-table" direction="column" gap="24px">
      <TableFiltersSection
        filters={inquiryTableFilters}
        key={timeStamp}
        onAddFilter={() => setIsOpenFilter(true)}
        onFiltersChange={handleFiltersChange}
        tableName="inquiry"
      />
      <ChakraFlex justifyContent="flex-end">
        <ChakraBox position="relative" width="350px">
          <InquiryFilter
            admissionsOperationsCoordinators={aoProviders}
            admissionsSpecialists={asProviders}
            admissionsSupervisors={supervisorProviders}
            channels={channelOptions}
            channelTypes={channelTypeMenuOptions.map((type) => type.label)}
            coverageStatusOptions={coverageStatusOptions}
            defaultFilters={inquiryTableFilters}
            informationSources={informationGatheredSourcesOptions}
            isOpen={isOpenFilter}
            maxHeight="calc(100vh - 400px)"
            medicalStatusOptions={medicalStatusOptions}
            onClose={() => setIsOpenFilter(false)}
            onSaveFilters={handleFiltersChange}
            patientServiceRepresentatives={psrProviders}
            payors={payors}
            qcAppointments={[
              QCAppointmentOptions.SCHEDULED,
              QCAppointmentOptions.NOT_SCHEDULED,
            ]}
            referralCoordinators={rcProviders}
            targetTreatmentPrograms={treatmentProgramOptions}
            timezones={timezones}
            viewCode={viewCode}
          />
        </ChakraBox>
      </ChakraFlex>

      <ChakraBox id="inquiry-table-scroll-container">
        <ChakraFlex
          direction="column"
          height={tableHeight}
          marginRight={`-${PAGE_LAYOUT.rightSpacing}`} // Compensate for the right padding of the Page component
          ref={tableRef}
        >
          <TableV2
            columns={columns}
            id="inquiry"
            isLoading={isTableLoading}
            onColumnSort={handleColumnSort}
            rows={isTableLoading ? loadingRows : rows}
          />
        </ChakraFlex>
        <PaginationV2
          currentPage={pageNumber}
          itemsPerPageOptions={DEFAULT_PAGINATION_CONFIG.pageSizeOptions}
          onItemsPerPageChange={handleItemPerPageChange}
          onPageChange={handlePageChange}
          tableName="inquiries"
          totalItems={totalCount}
        />
      </ChakraBox>
      <InquiryListConflictErrorModal
        isOpen={isConflictErrorModalOpen && !selectedInquiryId}
        onCancel={() => setIsConflictErrorModalOpen(false)}
        onConfirm={() => {
          setIsConflictErrorModalOpen(false);
          reloadInquiryList();
        }}
      />
    </ChakraFlex>
  );
};

export default InquiryViewsTable;
